import { createApi } from '@reduxjs/toolkit/query/react';
import { AvatarFavoritesResponse, AvatarUsersResponse, GetAvatarUsersRequest } from './models';
import { baseQueryWithReauth } from '../baseQueryWithReauth';

const avatarUsersTag = 'avatar_users';

export const avatarApi = createApi({
  reducerPath: avatarUsersTag,
  baseQuery: baseQueryWithReauth,
  tagTypes: [avatarUsersTag],
  endpoints: (build) => ({
    getAvatarUsers: build.query<AvatarUsersResponse, GetAvatarUsersRequest>({
      query: ({ page = 1, search, types } = {}) => {
        const params = new URLSearchParams();

        if (page) {
          params.set('page', page.toString());
        }

        if (search && search.trim().length >= 3) {
          params.set('filters[search]', search.trim());
        }

        types?.forEach((type) => {
          params.append('filters[types][]', type.toString());
        });

        const queryString = params.toString();

        return {
          url: `/avatar/users${queryString ? `?${queryString}` : ''}`,
          headers: {
            Accept: 'application/json',
          },
        };
      },
      providesTags: () => [{ type: avatarUsersTag, id: 'LIST' }],
    }),
    getAvatarFavorites: build.query<AvatarFavoritesResponse, void>({
      query: () => ({
        url: '/avatar/favorites',
        headers: {
          Accept: 'application/json',
        },
      }),
      providesTags: () => [{ type: avatarUsersTag, id: 'FAVORITES' }],
    }),
    addAvatarFavorite: build.mutation<void, number>({
      query: (userId) => ({
        url: `/avatar/favorites/${userId}`,
        method: 'POST',
        headers: {
          Accept: 'application/json',
        },
      }),
      invalidatesTags: [{ type: avatarUsersTag, id: 'FAVORITES' }],
    }),
    removeAvatarFavorite: build.mutation<void, number>({
      query: (userId) => ({
        url: `/avatar/favorites/${userId}`,
        method: 'DELETE',
        headers: {
          Accept: 'application/json',
        },
      }),
      invalidatesTags: [{ type: avatarUsersTag, id: 'FAVORITES' }],
    }),
  }),
});

export const {
  useGetAvatarUsersQuery,
  useGetAvatarFavoritesQuery,
  useAddAvatarFavoriteMutation,
  useRemoveAvatarFavoriteMutation,
} = avatarApi;
